import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { AutomationRuleTable } from './AutomationRuleTable';
import {
  filterAutomationRuleRows,
  sortAutomationRuleRows,
  toAutomationRuleRow,
} from './automationRuleTableModel';
import { registerEventsTourApi } from '../../eventsTour/eventsTourBridge';
import { RuleEditorSteps } from './RuleEditorSteps';
import type { RuleDraftPayload } from './ruleFormModel';
import {
  NEW_RULE_FEED_PARAM,
  draftForCatalogFeed,
} from 'components/Onboarding/ruleTemplates';

type EventAutomationSettingsPageProps = {
  onBack: () => void;
  parentLabel?: string;
};

/**
 * Search param that opens a specific rule in the editor (used by PRATUS AI's
 * draft handoff).
 */
export const RULE_ID_PARAM = 'ruleId';

export function EventAutomationSettingsPage({
  onBack,
  parentLabel = 'Events',
}: EventAutomationSettingsPageProps) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: rules = [],
    isLoading,
    isFetching,
    refetch,
  } = useOrgEventAutomationRules({
    enabled: true,
  });
  const ruleRefetchedRef = useRef(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createPayload, setCreatePayload] = useState<RuleDraftPayload>();
  const [search, setSearch] = useState('');
  const requestedRuleId = searchParams.get(RULE_ID_PARAM);
  const editingIdRef = useRef(editingId);
  editingIdRef.current = editingId;
  const creatingRef = useRef(false);

  const editingRule = rules.find((r) => r.id === editingId) ?? null;

  const rows = useMemo(
    () => sortAutomationRuleRows(rules.map(toAutomationRuleRow)),
    [rules]
  );
  const visibleRows = useMemo(
    () => filterAutomationRuleRows(rows, search),
    [rows, search]
  );

  const openCreate = useCallback(() => {
    if (editingIdRef.current || creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
  }, []);

  const closeCreate = useCallback(() => {
    creatingRef.current = false;
    setCreating(false);
    setCreatePayload(undefined);
  }, []);

  useEffect(() => {
    return registerEventsTourApi({
      addRule: async () => {
        openCreate();
      },
      closeEditor: () => {
        setEditingId(null);
        closeCreate();
      },
    });
  }, [openCreate, closeCreate]);

  // Deep link (?ruleId=…) from PRATUS AI's "Open in rule editor" after it drafts
  // a rule: once the list has loaded, open that rule and drop the param so
  // back-navigation / refresh return to the table.
  useEffect(() => {
    if (!requestedRuleId || isLoading || isFetching) return;
    const found = rules.some((r) => r.id === requestedRuleId);
    if (!found && !ruleRefetchedRef.current) {
      // A just-drafted rule may not be in a cached list yet — refetch once
      // before concluding it doesn't exist.
      ruleRefetchedRef.current = true;
      void refetch();
      return;
    }
    if (found) setEditingId(requestedRuleId);
    ruleRefetchedRef.current = false;
    setSearchParams(
      (params) => {
        params.delete(RULE_ID_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [requestedRuleId, isLoading, isFetching, rules, refetch, setSearchParams]);

  // Deep link (?newRuleFeed=…) from a report's Analytics "Create a rule".
  const requestedFeed = searchParams.get(NEW_RULE_FEED_PARAM);
  useEffect(() => {
    if (!requestedFeed) return;
    const draft = draftForCatalogFeed(requestedFeed);
    if (draft && !editingIdRef.current && !creatingRef.current) {
      setCreatePayload(draft);
      openCreate();
    }
    setSearchParams(
      (params) => {
        params.delete(NEW_RULE_FEED_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [requestedFeed, openCreate, setSearchParams]);

  return (
    <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:overflow-hidden tw:bg-background tw:text-foreground">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink render={<button type="button" />} onClick={onBack}>
              {parentLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {editingRule ? (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink
                  render={<button type="button" />}
                  onClick={() => setEditingId(null)}
                >
                  Automation Rules
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{editingRule.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : (
            <BreadcrumbItem>
              <BreadcrumbPage>Automation Rules</BreadcrumbPage>
            </BreadcrumbItem>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      {!editingRule ? (
        <div data-tour="event-settings-intro">
          <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
            Automation Rules
          </h2>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            When Pratus reads data feeds, if criteria are true, auto-create an
            event and optionally notify users and roles.
          </p>
        </div>
      ) : null}

      {editingRule ? (
        <RuleEditorSteps
          rule={editingRule}
          onClose={() => setEditingId(null)}
        />
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3">
          <div className="tw:flex tw:justify-start">
            <Button
              type="button"
              size="sm"
              className="tw:gap-1"
              data-tour="add-rule"
              disabled={!groupGuid}
              onClick={openCreate}
            >
              <Plus className="tw:h-4 tw:w-4" />
              Add Rule
            </Button>
          </div>

          <div className="tw:min-h-0 tw:flex-1 tw:space-y-2 tw:overflow-y-auto">
            {isLoading ? (
              <>
                <Skeleton className="tw:h-16 tw:w-full" />
                <Skeleton className="tw:h-16 tw:w-full" />
                <Skeleton className="tw:h-16 tw:w-full" />
              </>
            ) : rules.length === 0 ? (
              <Empty className="tw:border tw:border-dashed tw:border-border tw:p-8">
                <EmptyHeader>
                  <EmptyTitle className="tw:text-sm">
                    No automation rules
                  </EmptyTitle>
                  <EmptyDescription>
                    Add a rule to define When / If / Then / Notify for org
                    events.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <AutomationRuleTable
                rows={visibleRows}
                search={search}
                onSearchChange={setSearch}
                onSelect={setEditingId}
              />
            )}
          </div>
        </div>
      )}
      <FullScreenDialog
        open={creating}
        onOpenChange={(open) => {
          if (!open) closeCreate();
        }}
        title="Create automation rule"
        bodyClassName="tw:!flex tw:!flex-col tw:!overflow-hidden"
      >
        {creating ? (
          <RuleEditorSteps
            rule={null}
            initialPayload={createPayload}
            onClose={closeCreate}
          />
        ) : null}
      </FullScreenDialog>
    </div>
  );
}
